import {Tooltip} from '@mantine/core';
import Image from 'next/image';
import type React from 'react';
import {useState} from 'react';
import type {
  Company,
  EngineerCompany,
} from '../../../../../core/repositories/companyRepository';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {convertCompany} from '../../../usecase/convertCompany';
import {FollowCompanyButton} from '../FollowCompanyButton';

type Props = {
  company: Company | EngineerCompany;
  width: number;
  bgColorStyle: string;
};

// Company型とEngineerCompany型でプロパティ名が異なる
const isEngineerCompany = (
  company: Company | EngineerCompany,
): company is EngineerCompany => {
  return 'backgroundImageUrls' in company;
};

export const CompanyCard: React.FC<Props> = ({
  company,
  width,
  bgColorStyle,
}) => {
  const [showImage, setShowImage] = useState(
    isEngineerCompany(company)
      ? company.backgroundImageUrls['640x268'] && !company.isArchive
      : company.backgroundImageURLs['640x268'] && company.pageType !== 'study',
  );
  const [showLogo, setShowLogo] = useState(
    isEngineerCompany(company)
      ? company.logoUrls['140x105'] && !company.isArchive
      : company.logoURLs['140x105'] && company.pageType !== 'study',
  );

  const convertedCompany = convertCompany(company);

  const imgHeight = Math.floor((width / 640) * 268);
  const {isPC} = useMediaQuery();

  if (!company || company.id === 0) {
    return null;
  }

  return (
    <div
      className={`cursor-pointer hover:opacity-80 ${bgColorStyle} rounded-16`}
      style={{
        width: `${width}px`,
      }}
    >
      <CommonLink href={`/company/${convertedCompany.id}`}>
        <div
          className={`bg-black-4 pc:w-full w-[${width}px] h-[${imgHeight}px] overflow-hidden rounded-16 shadow-card`}
        >
          {showImage ? (
            <Image
              className="rounded-16 overflow-hidden line-clamp-1"
              src={convertedCompany.backgroundImg}
              width={width}
              height={imgHeight}
              alt={`${convertedCompany.name}のメイン写真`}
              onError={() => {
                setShowImage(false);
              }}
            />
          ) : (
            <div
              style={{
                height: `${imgHeight}px`,
              }}
            />
          )}
        </div>
      </CommonLink>
      <div className="flex justify-between">
        <CommonLink href={`/company/${convertedCompany.id}`}>
          <div className="mt-[-16px] relative z-10 ml-12">
            {showLogo && (
              <div className="bg-white p-4 rounded-4 shadow-card">
                <Image
                  src={convertedCompany.logoImg}
                  width={40}
                  height={40}
                  alt={`${convertedCompany.name}のロゴ画像`}
                  onError={() => {
                    setShowLogo(false);
                  }}
                />
              </div>
            )}
          </div>
        </CommonLink>
        <div className="flex mt-8 mr-12">
          <FollowCompanyButton
            companyId={convertedCompany.id}
            followCount={convertedCompany.followCount}
            isFollowed={convertedCompany.isFollowed}
          />
        </div>
      </div>

      <CommonLink
        href={`/company/${convertedCompany.id}`}
        className="mt-8 px-12 pc:px-0 block"
      >
        <div className="font-bold h-[42px] line-clamp-2 text-md">
          {convertedCompany.name}
        </div>
        <div
          className={`w-[${width - (isPC ? 0 : 16)}px] h-36 flex gap-8 text-black-3 text-sm py-4`}
        >
          {convertedCompany.isHiring && (
            <div className="rounded-16 bg-primary text-white px-8 py-4 mt-[2px] text-nowrap">
              募集あり
            </div>
          )}
          {convertedCompany.workAttractiveConditions[0] && (
            <div
              key={convertedCompany.workAttractiveConditions[0].id}
              className="flex justify-center items-center gap-4 py-4 h-full"
            >
              <div
                className="rounded-16 text-primary border border-primary px-8 line-clamp-1"
                style={{
                  lineHeight: '24px',
                }}
              >
                {convertedCompany.workAttractiveConditions[0].name}
              </div>
              {convertedCompany.workAttractiveConditions.length > 1 && (
                <div className="text-black-2 text-sm text-nowrap">
                  {`ほか${convertedCompany.workAttractiveConditions.length - 1}つ`}
                </div>
              )}
            </div>
          )}
        </div>
        <Tooltip label={convertedCompany.catchPhrase}>
          <div className="text-md line-clamp-1">
            {convertedCompany.catchPhrase}
          </div>
        </Tooltip>
      </CommonLink>
    </div>
  );
};
